import { useMemo, useCallback } from 'react';
import useSWR from 'swr';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import { filterByOrg } from '@/mockData/common/demoFilters';
import {
  ALL_CALENDAR_CONTACTS,
  ALL_CALENDAR_EVENTS,
  ALL_CALENDAR_TASKS,
} from '@/mockData';
import type { CalendarContact, CalendarEvent } from '@/types/calendar.types';
import { useNylasConnections } from '@/hooks/inbox/useNylas';
import { listIntegrationCalendarEventsApi } from '@/api/integrations';

export function useCalendarData() {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();

  // Real events: pull from the user's Nylas-connected calendar. The
  // useNylasConnections hook already fires from the inbox; SWR dedupes by
  // key so calling it again here costs nothing on the wire when the cache
  // is warm. When the user has no connection (or in demo mode) we fall
  // back to the mock dataset.
  const { connections } = useNylasConnections({ enabled: !isDemo });
  const calendarConnectionId = !isDemo
    ? connections.find((c) => c.scopes?.some((s) => s.includes('calendar')))?.connectionId
    : undefined;
  // Window the fetch around "today" instead of relying on the backend's
  // now→+30d default. The week grid often shows days earlier than today
  // (Mon-Wed when today is Friday) — without a wider window those days
  // render empty. Buffer ±30/+60 days so navigating one week back/forward
  // doesn't refetch. Refetch crosses midnight via the date-stamped SWR key.
  const todayKey = new Date().toISOString().slice(0, 10);
  const swrKey =
    !isDemo && calendarConnectionId
      ? ['nylas-calendar-events', calendarConnectionId, todayKey]
      : null;
  const { data: realEventsData } = useSWR(
    swrKey,
    async () => {
      const startOfTodayUtc = new Date(`${todayKey}T00:00:00.000Z`).getTime();
      const dayMs = 86_400_000;
      const timeMin = new Date(startOfTodayUtc - 30 * dayMs).toISOString();
      const timeMax = new Date(startOfTodayUtc + 60 * dayMs).toISOString();
      const resp = await listIntegrationCalendarEventsApi({
        connectionId: calendarConnectionId!,
        timeMin,
        timeMax,
        limit: 200,
      });
      return resp.events as CalendarEvent[];
    },
  );
  const realEvents = realEventsData ?? [];

  const events = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_EVENTS, selectedOrgId) : realEvents),
    [isDemo, selectedOrgId, realEvents],
  );
  const tasks = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_TASKS, selectedOrgId) : []),
    [isDemo, selectedOrgId],
  );
  const contacts = useMemo(
    () => (isDemo ? filterByOrg(ALL_CALENDAR_CONTACTS, selectedOrgId) : []),
    [isDemo, selectedOrgId],
  );

  const getContactById = useCallback(
    (id: string): CalendarContact | undefined =>
      isDemo ? contacts.find((contact) => contact.id === id) : undefined,
    [contacts, isDemo],
  );

  const getUpcomingEvents = useCallback(
    (limit?: number): CalendarEvent[] => {
      // Filter to events at or after "now" before sorting. The fetch
      // window now spans 30 days back through 60 days forward to populate
      // the week grid for prior-week navigation, so a naive sort-ascending
      // would surface past events as "upcoming."
      const nowMs = Date.now();
      return events
        .filter((e) => new Date(`${e.date}T${e.startTime}`).getTime() >= nowMs)
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.startTime}`);
          const dateB = new Date(`${b.date}T${b.startTime}`);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, limit ?? 5);
    },
    [events],
  );

  return { events, tasks, contacts, getContactById, getUpcomingEvents };
}
